/**
 * terminalsSnapshot — turn the Terminals view's on-screen panes into a
 * human-readable SpaContext snapshot (2026-07-01, see+control generalization).
 *
 * Lets the voice ("BRIDGE") and Pi agents SEE which agent terminals are open
 * (agent type · display name · state) instead of guessing. Pure +
 * framework-free so it unit-tests directly.
 */
import type { SpaContext } from '@/stores/spaContextStore';
import type { PaneRecord, AgentType } from '@/stores/paneStore';

export const TERMINALS_VIEW_ID = 'terminals';

const AGENT_LABELS: Record<AgentType, string> = {
  openclaw_shell: 'OpenClaw',
  opencode_shell: 'OpenCode',
  hermes_agent: 'Hermes Agent',
  shell: 'Bash',
  pi_shell: 'Pi',
};

function agentLabel(t: AgentType): string {
  return AGENT_LABELS[t] ?? t;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Build the on-screen snapshot for the Terminals view. */
export function buildTerminalsSnapshot(panes: PaneRecord[]): SpaContext {
  const items = panes.map((p) => {
    const name = p.display_name ?? p.name;
    return `${agentLabel(p.agent_type)} · ${name} · ${p.state}`;
  });
  const open = panes.filter((p) => p.state === 'open').length;
  return {
    view: TERMINALS_VIEW_ID,
    activeView: TERMINALS_VIEW_ID,
    title: 'Terminals',
    detail:
      panes.length === 0
        ? 'No agent terminals open'
        : `${plural(panes.length, 'agent terminal', 'agent terminals')} (${open} open)`,
    items,
  };
}
